import Service, { service } from '@ember/service';

import {
  isAbortError,
  isBadRequestResponse,
  isConflictResponse,
  isForbiddenResponse,
  isGoneResponse,
  isInvalidResponse,
  isNotFoundResponse,
  isServerErrorResponse,
  isUnauthorizedResponse,
} from 'ember-fetch/errors';
import { serializeQueryParams } from 'ember-fetch/utils/serialize-query-params';

import config from 'umg-ember-backend/config/environment';
import {
  AbortError,
  BadRequestError,
  ConflictError,
  FetchError,
  ForbiddenError,
  GoneError,
  InvalidError,
  NotFoundError,
  ServerError,
  UnauthorizedError,
} from 'umg-ember-backend/utils/errors';

export default class FetchService extends Service {
  // Services
  @service session;
  @service msal;

  // Defaults
  host = config.API.host;
  namespace = config.API.namespace;

  // Functions
  async getHeaders() {
    const headers = {};
    const isAuthenticated = this.session.isAuthenticated;
    headers['Content-Type'] = 'application/json; charset=utf-8';

    if (!isAuthenticated) {
      return headers;
    }

    const { authenticator, token } = this.session.data.authenticated;

    if (authenticator === 'authenticator:microsoft') {
      try {
        const tokenResponse = await this.msal.getAccessToken();

        headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
      } catch {
        this.session.invalidate();
      }
    } else {
      headers['X-UMG-BACKEND-TOKEN'] = token;
    }

    return headers;
  }

  async raw(url, options = {}) {
    const headers = await this.getHeaders();

    const requestOptions = {
      method: options.method ?? 'GET',
      headers: { ...headers, ...options.headers },
    };

    const abortController = new AbortController();
    requestOptions.signal = abortController.signal;

    let builtURL = this._buildURL(url, options);
    if (options.data) {
      let { data } = options;

      if (options.processData === false) {
        requestOptions.body = data;
      } else {
        if (requestOptions.method === 'GET') {
          builtURL = `${builtURL}?${new URLSearchParams(
            serializeQueryParams(data),
          )}`;
        } else {
          requestOptions.body = JSON.stringify(data);
        }
      }
    }

    if (options.signal) {
      options.signal.addEventListener('abort', () => abortController.abort());
    }

    let timeout;
    if (options.timeout) {
      timeout = setTimeout(() => abortController.abort(), options.timeout);
    }

    let response = await fetch(builtURL, requestOptions);

    if (timeout) {
      clearTimeout(timeout);
    }

    if (isUnauthorizedResponse(response)) {
      this.session.invalidate();
    }

    let payload;
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      payload = response.json();
    } else if (
      contentType.includes('text/zip') ||
      contentType.includes('text/csv')
    ) {
      payload = response.blob();
    } else {
      payload = response.text();
    }

    if (response.ok) {
      return { response, payload, requestOptions, builtURL };
    } else {
      const resolvedPayload = await payload;

      if (typeof resolvedPayload?.errors === 'object') {
        resolvedPayload.errors = [
          {
            status: resolvedPayload.errors.status,
            title: resolvedPayload.errors.title,
            detail: resolvedPayload.errors.message,
          },
        ];

        throw resolvedPayload;
      }

      throw this._createCorrectError(
        response,
        resolvedPayload,
        requestOptions,
        url,
      );
    }
  }

  async request(url, options = {}) {
    const { payload } = await this.raw(url, options);

    return payload;
  }

  post(url, options) {
    return this.request(url, { ...options, method: 'POST' });
  }

  put(url, options) {
    return this.request(url, { ...options, method: 'PUT' });
  }

  patch(url, options) {
    return this.request(url, { ...options, method: 'PATCH' });
  }

  delete(url, options) {
    return this.request(url, { ...options, method: 'DELETE' });
  }

  _buildURL(url, options = {}) {
    const urlParts = [];

    let host = options.host ?? this.host ?? '';
    urlParts.push(host);

    let namespace = options.namespace ?? this.namespace;
    if (namespace) {
      urlParts.push(namespace);
    }

    urlParts.push(url.replace(/^\/|\/$/g, ''));

    return urlParts.join('/');
  }

  _createCorrectError(response, payload, requestOptions, url) {
    let error;

    if (isUnauthorizedResponse(response)) {
      error = new UnauthorizedError(payload);
    } else if (isForbiddenResponse(response)) {
      error = new ForbiddenError(payload);
    } else if (isInvalidResponse(response)) {
      error = new InvalidError(payload);
    } else if (isBadRequestResponse(response)) {
      error = new BadRequestError(payload);
    } else if (isNotFoundResponse(response)) {
      error = new NotFoundError(payload);
    } else if (isGoneResponse(response)) {
      error = new GoneError(payload);
    } else if (isAbortError(response)) {
      error = new AbortError();
    } else if (isConflictResponse(response)) {
      error = new ConflictError(payload);
    } else if (isServerErrorResponse(response)) {
      error = new ServerError(payload, response.status);
    } else {
      const detailedMessage = this.generateDetailedMessage(
        response.status,
        payload,
        requestOptions.headers['Content-Type'],
        requestOptions.method,
        url,
      );

      error = new FetchError(payload, detailedMessage, response.status);
    }

    return error;
  }
}
